// ============================================
// Isolated Video — shared § 106 playback-frame factory
// ============================================
//
// BUG_PATTERNS § 106: Chromium permanently retains each played clip's
// decoded-frame pool (media/frame_buffers, ~13MB/clip renderer RSS under
// software decode, GPU-process memory under hardware decode) when the
// <video> lives in the main document — the canonical pause +
// removeAttribute('src') + load() teardown does NOT free it, and the
// elements are provably GC'd while the pools survive (probe-verified on
// shipped 0.2.9.1). Hosting the clip in a throwaway same-origin <iframe>
// whose <video> is created BY THE FRAME'S DOCUMENT fixes it: removing the
// frame destroys the document and deterministically frees the
// WebMediaPlayer + frame pool.
//
// Consumed by story-content.js (_expandVideo modal copy) and gallery.js
// (fullscreen viewer). The inline story clip path (_addVideo) implements
// the same pattern with its own §91-aware wiring and imports only the
// voice-clip set from here.
//
// Voice clips (HYPNO_VOICE_CLIPS) must NEVER be frame-hosted: their Web
// Audio gain routing (audioManager.attachHypnoVideo) and every §91 audio
// stop-surface (querySelectorAll('video') sweeps) depend on the main-
// document path — and a handful of hypno beats per session cannot drive
// the leak. Callers pass { forceInline: isVoiceClip(name) }.
//
// Kill-switch: localStorage PnO_IsolatedVideo = '0' falls back to a plain
// main-document <video> everywhere (also taken automatically when the
// frame's document is inaccessible — exotic embedders / sandboxed pages).

import { appearanceState } from '../../systems/appearance.js';

// The clips that carry a voice track. Their audio plays through the game's
// Web Audio path; the element must stay in the main document. Audio files:
// assets/audio/hypno/<name>.(ogg|m4a). Keep in sync with that folder.
export const HYPNO_VOICE_CLIPS = new Set([
    // Laptop hypno (Computer.ink)
    'hypno_surrender_induction', 'hypno_spiral_obey', 'hypno_deep_conditioning',
    'hypno_deep_mantra', 'hypno_sasha_induction', 'hypno_sasha_session',
    'hypno_surrender_deep', 'hypno_surrender_protocol',
    // HypnoMind app (HypnoMind.ink, 0.2.9) — flash clips carry the conditioning
    // voice; registering them routes the audio through the Web Audio gain so it
    // plays + stops reliably (incl. Android), same as the laptop hypno.
    'hmcat_fem_flash_1', 'hmcat_fem_flash_2', 'hmcat_fem_flash_3',
    'hmcat_obey_flash_1', 'hmcat_obey_flash_2', 'hmcat_obey_flash_3',
    'hmcat_oral_flash_1', 'hmcat_oral_flash_2', 'hmcat_oral_flash_3',
    'hmcat_bimbo_flash_1', 'hmcat_bimbo_flash_2', 'hmcat_bimbo_flash_3',
    'hmcat_exhib_flash_1', 'hmcat_exhib_flash_2', 'hmcat_exhib_flash_3',
    'hmcat_anal_flash_1', 'hmcat_anal_flash_2', 'hmcat_anal_flash_3',
]);

/** True when `nameOrSrc` (clip name, path or URL, any variant suffix)
 *  refers to a voice-bearing clip that must stay in the main document. */
export function isVoiceClip(nameOrSrc) {
    if (!nameOrSrc) return false;
    let base = String(nameOrSrc).split('/').pop().replace(/\.\w+$/, '');
    if (HYPNO_VOICE_CLIPS.has(base)) return true;
    try { base = appearanceState.stripVariantSuffix(base); } catch (e) { /* keep as-is */ }
    return HYPNO_VOICE_CLIPS.has(base);
}

/**
 * Create a clip element, isolated in a playback frame when allowed.
 *
 * @param {object} opts
 * @param {string} opts.hostClassName  classes for the INSERTED element (frame
 *        or fallback video) — carries the caller's sizing/fade CSS.
 * @param {boolean} [opts.forceInline] true = skip the frame (voice clips).
 * @param {(host: HTMLElement) => void} insert  called exactly once with the
 *        host element; MUST connect it to the DOM (an iframe only gets a
 *        document once inserted).
 * @returns {{ host: HTMLElement, video: HTMLVideoElement, frame: HTMLIFrameElement|null }}
 *        `video` is the element to configure (src/controls/volume/listeners —
 *        same-origin cross-document access is fine). For frame clips set
 *        src ABSOLUTE (new URL(src, document.baseURI).href). Teardown of a
 *        frame clip = remove the frame (that IS the § 106 fix).
 */
export function createIsolatedClip(opts, insert) {
    let frame = null;
    let video = null;
    let wantFrame = !opts.forceInline;
    try { if (localStorage.getItem('PnO_IsolatedVideo') === '0') wantFrame = false; } catch (e) {}
    if (wantFrame) {
        const fr = document.createElement('iframe');
        fr.className = opts.hostClassName || '';
        fr.setAttribute('scrolling', 'no');
        fr.setAttribute('aria-hidden', 'true');
        fr.setAttribute('tabindex', '-1');
        insert(fr);
        try {
            const idoc = fr.contentDocument;
            idoc.documentElement.style.background = 'transparent';
            idoc.body.style.cssText = 'margin:0;background:transparent;overflow:hidden;display:flex;align-items:center;justify-content:center;height:100vh;';
            video = idoc.createElement('video');   // owned by the frame's document — dies with it
            video.style.cssText = 'width:100%;height:100%;object-fit:contain;display:block;';
            idoc.body.appendChild(video);
            fr._pnoVideo = video;                  // teardown/sweep helpers mapping
            frame = fr;
        } catch (e) {
            try { fr.remove(); } catch (e2) { /* never inserted */ }
            frame = null;
            video = null;
        }
    }
    if (!video) {
        video = document.createElement('video');
        video.className = opts.hostClassName || '';
        insert(video);
    }
    return { host: frame || video, video, frame };
}

/**
 * Release every clip under `root` in the MAIN document tree: plain <video>
 * elements get the canonical unload; playback frames are removed outright
 * (document teardown — frees the § 106 pools). `frameSelector` scopes which
 * frames belong to the caller (e.g. 'iframe.image-modal-img').
 * @returns {number} clips released
 */
export function releaseClipsIn(root, frameSelector) {
    if (!root) return 0;
    let n = 0;
    root.querySelectorAll(frameSelector).forEach(fr => {
        try {
            const v = fr._pnoVideo || fr.contentDocument?.querySelector('video');
            if (v) { v.pause(); v.muted = true; }
        } catch (e) { /* dead frame */ }
        fr.remove();
        n++;
    });
    return n;
}
